import { useState, useEffect, useRef } from "react"
import axios from "axios"
import { Auth } from "aws-amplify"
import { PARTS_CATALOG_URL } from "utils/functions"

// scrap-cype monta las rutas privadas por entorno (/{dev,stg,prod}/private/cype/*), cada
// una con su authorizer contra el pool Cognito de ese entorno. Sin la variable se usan las
// rutas públicas /cype/*, sin auth pero limitadas en API Gateway a 5 req/s (burst 10) para
// TODOS los llamantes combinados.
const CATALOG_ENV = process.env.REACT_APP_PARTS_CATALOG_ENV || ""

// El modo `page` se sirve desde /part-browser, que es una ruta pública (ver
// `publicRoutes` en src/routes/index.js) pensada para embeberse por iframe en la web
// pública: ahí no hay sesión Cognito, así que las rutas privadas no son una opción
// ni aunque la variable esté definida. Sólo el modo `modal` vive dentro de la app autenticada.
const usesPrivateRoutes = mode => mode === "modal" && CATALOG_ENV !== ""

const ERR_AUTH =
  "Tu sesión ha caducado. Vuelve a iniciar sesión para buscar partidas."
const ERR_THROTTLED =
  "Demasiadas búsquedas seguidas. Espera unos segundos y vuelve a intentarlo."
const ERR_GENERIC =
  "No se ha podido conectar con el catálogo de partidas. Inténtalo de nuevo."

function baseUrl(isPrivate) {
  return `${PARTS_CATALOG_URL}${isPrivate ? `/${CATALOG_ENV}/private/cype` : "/cype"}`
}

async function authHeaders(isPrivate) {
  if (!isPrivate) return {}
  const jwt = (await Auth.currentSession()).getIdToken().getJwtToken()
  return { Authorization: `Bearer ${jwt}` }
}

function describeError(err, isPrivate) {
  const status = err?.response?.status
  if (status === 401 || status === 403) return ERR_AUTH
  // El 503 también es throttling: cuando se agota reservedConcurrency de la Lambda, HTTP
  // API lo devuelve como 503, no 429.
  if (status === 429 || status === 503) return ERR_THROTTLED
  // Auth.currentSession() rechaza cuando no hay sesión válida, y lo hace sin `response`
  // ni `request`: es un fallo de sesión, no de red. Sólo aplica en modo privado — en
  // público no se pide sesión, y decirle "vuelve a iniciar sesión" a un visitante
  // anónimo del iframe no tendría sentido.
  if (isPrivate && !err?.response && !err?.request) return ERR_AUTH
  return ERR_GENERIC
}

// El backend responde 400 ante un valor no numérico o negativo, y la caja de texto deja
// escribir cualquier cosa: sólo se envía lo que ya sabemos que acepta.
function priceParam(value) {
  if (value === "" || value === null || value === undefined) return null
  const n = Number(value)
  return Number.isFinite(n) && n >= 0 ? n : null
}

function buildParams(q, sectionFilter, unitFilter, minPrice, maxPrice, cursor) {
  const params = { page_size: 50 }
  if (q) params.q = q
  if (sectionFilter) params.section = sectionFilter
  if (unitFilter) params.unit = unitFilter
  const min = priceParam(minPrice)
  const max = priceParam(maxPrice)
  if (min !== null) params.min_price = min
  if (max !== null) params.max_price = max
  if (cursor) params.cursor = cursor
  return params
}

// Los contadores del facet `unit` sólo son completos cuando NO hay filtro de unidad:
// con `unit=m²` el backend agrega sobre el resultado ya filtrado y devuelve ese único
// bucket. Se cachean los del resultado sin filtrar y se reutilizan mientras el resto de
// la búsqueda no cambie: los contadores dependen de q, section y del rango de precio.
function unitsSignature(q, sectionFilter, minPrice, maxPrice) {
  return `${q}|${sectionFilter || ""}|${minPrice || ""}|${maxPrice || ""}`
}

function resolveUnits(cacheRef, signature, buckets, unitFilter) {
  const counts = new Map((buckets || []).map(b => [b.key, b.count]))

  if (!unitFilter) {
    cacheRef.current = { signature, counts }
    return [...counts].map(([key, count]) => ({ key, count }))
  }

  const cached = cacheRef.current
  if (cached && cached.signature === signature) {
    return [...cached.counts].map(([key, count]) => ({ key, count }))
  }

  // Primera carga ya con unidad filtrada (sin caché previa): sólo se conoce ese bucket.
  return [...counts].map(([key, count]) => ({ key, count }))
}

function mapItem(raw) {
  return {
    id: raw.id,
    code: raw.code,
    shortDescription: raw.shortDescription,
    longDescription: raw.longDescription,
    unit: raw.unit,
    unitPrice: raw.unitPrice,
    updatedAt: raw.updatedAt
      ? raw.updatedAt.slice(0, 10).split("-").reverse().join("/")
      : "",
    source: raw.source || "CYPE generador de precios",
    section: raw.section,
    hasBreakdown: raw.hasBreakdown,
    // `chapter_path` sólo viene en /cype/item, no en el listado: se rellena al abrir
    // el detalle (fetchDetail), igual que el desglose.
    chapterPath: [],
    favorite: false,
    actualizable: true,
    breakdown: raw.breakdown || [],
    targetCost: null,
    clientPrice: null,
    margin: null,
    originBudgetName: null,
    originChapter: null,
    usageCount: 0,
  }
}

const useCypeSearch = (
  mode,
  q,
  sectionFilter,
  unitFilter,
  minPrice,
  maxPrice,
) => {
  const isPrivate = usesPrivateRoutes(mode)
  const base = baseUrl(isPrivate)

  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(false)
  const [total, setTotal] = useState(0)
  const [nextCursor, setNextCursor] = useState(null)
  const [loadingMore, setLoadingMore] = useState(false)
  const [error, setError] = useState(null)
  const [units, setUnits] = useState([])

  const cancelRef = useRef(null)
  const unitCountsRef = useRef(null)

  useEffect(() => {
    const trimmed = (q || "").trim()

    if (trimmed.length < 3) {
      if (cancelRef.current) cancelRef.current()
      setItems([])
      setTotal(0)
      setNextCursor(null)
      setLoading(false)
      setError(null)
      setUnits([])
      unitCountsRef.current = null
      return
    }

    const timer = setTimeout(async () => {
      if (cancelRef.current) cancelRef.current()

      // El CancelToken se crea antes de esperar el JWT: si entra otra búsqueda mientras
      // se resuelve la sesión, cancelRef ya apunta a este token y axios aborta la petición.
      const { token: cancelToken, cancel } = axios.CancelToken.source()
      cancelRef.current = cancel

      setLoading(true)
      setNextCursor(null)
      setError(null)

      try {
        const signature = unitsSignature(
          trimmed,
          sectionFilter,
          minPrice,
          maxPrice,
        )
        const cachedCounts = unitCountsRef.current?.signature === signature
        const params = buildParams(
          trimmed,
          sectionFilter,
          unitFilter,
          minPrice,
          maxPrice,
          null,
        )
        // Con unidad filtrada y contadores ya cacheados el facet no aporta nada nuevo,
        // así que se evita la agregación en OpenSearch.
        if (!unitFilter || !cachedCounts) params.facets = "unit"

        const headers = await authHeaders(isPrivate)
        const res = await axios.get(`${base}/search`, {
          params,
          headers,
          cancelToken,
        })
        setItems(res.data.items.map(mapItem))
        setTotal(res.data.total)
        setNextCursor(res.data.next_cursor || null)
        setUnits(
          resolveUnits(
            unitCountsRef,
            signature,
            res.data.facets?.unit,
            unitFilter,
          ),
        )
      } catch (err) {
        if (axios.isCancel(err)) return
        console.error("useCypeSearch error:", err)
        setItems([])
        setTotal(0)
        setError(describeError(err, isPrivate))
      } finally {
        setLoading(false)
      }
    }, 400)

    return () => clearTimeout(timer)
  }, [q, sectionFilter, unitFilter, minPrice, maxPrice])

  const fetchMore = async () => {
    if (!nextCursor || loadingMore || !q) return
    setLoadingMore(true)
    try {
      const headers = await authHeaders(isPrivate)
      const res = await axios.get(`${base}/search`, {
        params: buildParams(
          q,
          sectionFilter,
          unitFilter,
          minPrice,
          maxPrice,
          nextCursor,
        ),
        headers,
      })
      setItems(prev => [...prev, ...res.data.items.map(mapItem)])
      setNextCursor(res.data.next_cursor || null)
      setError(null)
    } catch (err) {
      console.error("useCypeSearch fetchMore error:", err)
      setError(describeError(err, isPrivate))
    } finally {
      setLoadingMore(false)
    }
  }

  const fetchDetail = async id => {
    try {
      const headers = await authHeaders(isPrivate)
      const res = await axios.get(`${base}/item`, { params: { id }, headers })
      setItems(prev =>
        prev.map(item =>
          item.id === id
            ? {
                ...item,
                breakdown: res.data.breakdown || [],
                chapterPath: res.data.chapter_path || [],
              }
            : item,
        ),
      )
      return res.data
    } catch (err) {
      console.error("useCypeSearch fetchDetail error:", err)
      return null
    }
  }

  return {
    items,
    loading,
    loadingMore,
    total,
    error,
    units,
    hasMore: !!nextCursor,
    fetchMore,
    fetchDetail,
  }
}

export default useCypeSearch
